/**
 * O cadastro do cliente, e a edição dele no lugar (H08).
 *
 * ── Por que editar aqui, e não num diálogo ────────────────────────────────
 *
 * O cadastro é um bloco pequeno numa tela de consulta. Um diálogo tamparia a
 * ficha justamente enquanto se corrige um campo dela — e quem edita costuma
 * estar conferindo o que está escrito ao lado, na conversa ou na oportunidade.
 *
 * ── O que se manda, e o que não ───────────────────────────────────────────
 *
 * Só os campos que mudaram. Um formulário que manda tudo a cada salvamento
 * apagaria, com um `null`, o que a pessoa nem tocou — e o contrato distingue
 * as duas coisas de propósito: ausente é "não mexer", `null` é apagar.
 *
 * ── O documento nasce vazio, e vazio não apaga ────────────────────────────
 *
 * O número não vem do servidor: ele é guardado cifrado, e ver de novo exige
 * confirmar identidade. Então o campo começa em branco mesmo quando já há um
 * documento cadastrado — e por isso deixá-lo em branco não pode significar
 * "apague". Quem quer tirar usa o botão de apagar, que diz o que vai fazer.
 *
 * ── Cidade não está aqui ──────────────────────────────────────────────────
 *
 * `person.city` não existe no motor de CRM (A01 §6, verificado na A10 §6.5);
 * cidade, lá, é da empresa. Mostrar um campo que não tem onde ser gravado
 * seria prometer o que o produto não cumpre.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AtualizarCliente, Cliente360, MudancaDeCampos } from '@cliqcrm/contracts';
import { useApi, useCamposPersonalizados } from '@/shared/api/consultas';
import { CamposEditaveis, obrigatoriosVazios } from '@/entities/campos/campo-personalizado';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { chaves } from '@/shared/api/chaves';
import { Botao, Campo } from '@/shared/ui';
import { usePreferencias } from '@/shared/sessao/contexto';

type ClienteDo360 = Cliente360['cliente'];

/** O que estava escrito, para o formulário nascer com o valor de hoje. */
function comoTexto(valor: string | null): string {
  return valor ?? '';
}

/**
 * A diferença entre o que estava e o que ficou.
 *
 * Campo intocado não entra; campo esvaziado entra como `null`, que é o que
 * apaga do outro lado. O nome não aceita vazio: um cliente sem nome nenhum
 * some da lista e da busca.
 */
export function diferenca(
  antes: ClienteDo360,
  depois: {
    nome: string;
    cargo: string;
    email: string;
    telefone: string;
    documento: string;
    apagarDocumento?: boolean;
  },
): AtualizarCliente {
  const mudanca: Record<string, string | null> = {};
  const nome = depois.nome.trim();
  if (nome !== '' && nome !== comoTexto(antes.nome_exibicao)) mudanca['nome'] = nome;

  for (const [campo, valorNovo, valorAntigo] of [
    ['cargo', depois.cargo, antes.cargo],
    ['email', depois.email, antes.email],
    ['telefone', depois.telefone, antes.telefone_exibicao],
  ] as const) {
    const limpo = valorNovo.trim();
    if (limpo === comoTexto(valorAntigo)) continue;
    mudanca[campo] = limpo === '' ? null : limpo;
  }

  // O documento é o único campo em que vazio NÃO é apagar — ver o cabeçalho.
  const documento = depois.documento.trim();
  if (documento !== '') mudanca['documento'] = documento;
  else if (depois.apagarDocumento) mudanca['documento'] = null;

  return mudanca as AtualizarCliente;
}

export function CadastroEmEdicao({
  clienteId,
  cliente,
  aoFechar,
}: {
  clienteId: string;
  cliente: ClienteDo360;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [nome, definirNome] = useState(comoTexto(cliente.nome_exibicao));
  const [cargo, definirCargo] = useState(comoTexto(cliente.cargo));
  const [email, definirEmail] = useState(comoTexto(cliente.email));
  const [telefone, definirTelefone] = useState(comoTexto(cliente.telefone_exibicao));
  const [documento, definirDocumento] = useState('');
  const [apagarDocumento, definirApagarDocumento] = useState(false);
  // Os campos personalizados (#568): só o que a pessoa mexeu vai no pedido.
  const campos = useCamposPersonalizados('cliente');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});
  const faltando = obrigatoriosVazios(campos.data?.itens ?? [], cliente.campos, mudancaDosCampos);

  /*
   * O campo de documento só existe quando a REVENDA usa documento (H18).
   *
   * Esconder, e não desabilitar: um campo cinza pergunta "por que não posso?"
   * a quem não tem como responder — quem liga isso é quem administra a
   * revenda, e não quem está atendendo. E a API recusa de qualquer forma
   * (D23), então mostrá-lo seria oferecer um caminho que termina em erro.
   */
  const { usar_documento_na_identidade: usaDocumento } = usePreferencias();
  /** Quantos cadastros o documento fez o sistema suspeitar — `null` até salvar. */
  const [candidatos, definirCandidatos] = useState<number | null>(null);

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      const corpo = diferenca(cliente, {
        nome,
        cargo,
        email,
        telefone,
        documento,
        apagarDocumento,
      });
      const comCampos =
        Object.keys(mudancaDosCampos).length > 0 ? { ...corpo, campos: mudancaDosCampos } : corpo;
      if (Object.keys(comCampos).length === 0) return null;
      return api.atualizarCliente({ caminho: { id: clienteId }, corpo: comCampos });
    },
    onSuccess: (salvo) => {
      /*
       * O 360 inteiro, e não só este bloco: o nome também está no cabeçalho da
       * tela, e o cargo na linha de apoio embaixo dele. Invalidar a consulta é
       * o que faz os três contarem a mesma história.
       */
      void cache.invalidateQueries({ queryKey: chaves.cliente360(clienteId) });

      /*
       * O formulário FICA ABERTO quando o documento levantou suspeita.
       *
       * Fechar levaria a pessoa de volta ao 360 com um aviso que ela não leria
       * — e este é o aviso que a Armadilha da C10 existe para dar: que nada foi
       * unido, e que há uma decisão esperando por gente. Fechar depois de dizer
       * isso é o mesmo que não dizer.
       */
      if (salvo && salvo.candidatos_de_fusao > 0) {
        definirCandidatos(salvo.candidatos_de_fusao);
        return;
      }
      aoFechar();
    },
  });

  return (
    <form
      className="c360__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        salvar.mutate();
      }}
    >
      <Campo
        id="cadastro-nome"
        rotulo="Nome"
        type="text"
        value={nome}
        maxLength={200}
        required
        onChange={(e) => definirNome(e.currentTarget.value)}
      />
      <Campo
        id="cadastro-cargo"
        rotulo="Cargo"
        type="text"
        value={cargo}
        maxLength={120}
        onChange={(e) => definirCargo(e.currentTarget.value)}
      />
      <Campo
        id="cadastro-email"
        rotulo="E-mail"
        type="email"
        value={email}
        maxLength={200}
        onChange={(e) => definirEmail(e.currentTarget.value)}
      />
      <Campo
        id="cadastro-telefone"
        rotulo="Telefone"
        type="tel"
        value={telefone}
        maxLength={40}
        ajuda="Com DDD."
        onChange={(e) => definirTelefone(e.currentTarget.value)}
      />

      <CamposEditaveis
        id="cadastro-campo"
        campos={campos.data?.itens ?? []}
        valores={cliente.campos}
        mudanca={mudancaDosCampos}
        aoMudar={definirMudancaDosCampos}
      />

      {usaDocumento && (
        <>
          <Campo
            id="cadastro-documento"
            rotulo="CPF ou CNPJ"
            type="text"
            inputMode="numeric"
            value={documento}
            maxLength={30}
            disabled={apagarDocumento}
            placeholder={cliente.documento.presente ? 'já cadastrado' : ''}
            ajuda={
              apagarDocumento
                ? 'Será apagado ao salvar.'
                : cliente.documento.presente
                  ? 'Guardado cifrado — não dá para mostrar aqui. Digite para substituir.'
                  : 'Guardado cifrado. Só o dígito verificador é conferido na hora.'
            }
            onChange={(e) => definirDocumento(e.currentTarget.value)}
          />
          {cliente.documento.presente && (
            <div>
              <Botao
                variante="fantasma"
                miudo
                type="button"
                onClick={() => {
                  definirApagarDocumento((estava) => !estava);
                  definirDocumento('');
                }}
              >
                {apagarDocumento ? 'Manter o documento' : 'Apagar o documento'}
              </Botao>
            </div>
          )}

          {/*
        O aviso vem ANTES de salvar, e só quando há documento a informar.

        Informar um CPF pode revelar que dois cadastros são a mesma pessoa — e
        quem digita precisa saber disso antes, não depois. A frase evita a
        leitura errada que a Armadilha da C10 nomeia: que o sistema vai
        JUNTAR os cadastros sozinho. Ele não junta; ele pergunta.
      */}
          {documento.trim() !== '' && (
            <p className="c360__aviso">
              Se este documento já estiver em outro cadastro, os dois vão para a fila de{' '}
              <b>revisão de identidade</b> — ninguém é unido automaticamente.
            </p>
          )}
        </>
      )}

      {/*
        O que aconteceu DE VERDADE, e não o que a pessoa teme que tenha
        acontecido.

        "Encontramos um cadastro igual" faz concluir que algo já foi feito.
        Nada foi: documento igual vale 0,95 e mesmo assim não funde sozinho,
        porque um dígito errado transformaria duas pessoas em uma. A frase
        precisa dizer onde a decisão está e de quem ela é.
      */}
      {candidatos !== null && (
        <p className="c360__aviso c360__aviso--fila" role="status">
          Salvo. Este documento já estava em{' '}
          <b>{candidatos === 1 ? 'outro cadastro' : `outros ${candidatos} cadastros`}</b>, e o par
          foi para a fila de <b>revisão de identidade</b>. Nada foi unido — alguém precisa olhar e
          decidir.
        </p>
      )}

      {salvar.isError && (
        <p className="c360__erro" role="alert">
          {recadoDeErro(salvar.error, { generica: 'Não foi possível salvar o cadastro.' })}
        </p>
      )}

      <div className="c360__acoes-do-formulario">
        <Botao
          variante="primario"
          type="submit"
          desabilitado={salvar.isPending || faltando.length > 0}
          motivo={
            salvar.isPending
              ? 'Salvando…'
              : `Preencha ${faltando.map((campo) => campo.nome).join(', ')}.`
          }
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </Botao>
        <Botao variante="fantasma" type="button" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
